import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { app, API_BASE_URL } from '../../firebase/firebaseConfig';
import {
  HCard,
  HCardButton,
  HigloIcon,
  HChip,
  HPrimaryButton,
  HSecondaryButton,
  HField,
} from '../h';
import { useHiglos } from './useHiglos';
import { IncomingInvites } from './IncomingInvites';

/* v9 5.1 — Higlos tab list.

   Summary strip, a lede explaining what a Higlo is, then a 2-row card for
   each Higlo (icon + name + count subline, plus chips of the Hearth
   names). A dashed "+ Start a new Higlo" CTA sits below the list; when
   tapped it SWAPS IN PLACE with an inline create form so the user doesn't
   have to hunt for it — creating feels like filling in the box where the
   button was.

   Props:
     accountUUID       — passed through to the detail routes.
     owningFamilies    — families the signed-in user owns (we read their
                         igloos subcollections to build the list).
     primaryFamilyID   — used by /user/createIgloo + invite subscriptions.
*/
export function HiglosTab({ accountUUID, owningFamilies, primaryFamilyID }) {
  const navigate = useNavigate();
  const higlos = useHiglos({ owningFamilies, accountUUID, primaryFamilyID });
  const entries = Object.values(higlos || {});
  const total = entries.length;
  // An active Higlo is one where at least one of the user's Hearths has a
  // position assigned in it (Left/Centre/Right).
  const active = entries.filter((h) =>
    (h.ownedMemberships || []).some((m) => m.position),
  ).length;

  const summary = total === 0
    ? 'No Higlos yet'
    : `${total} Higlo${total === 1 ? '' : 's'}${active ? ` · ${active} active` : ''}`;

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const openCreate = () => {
    setCreateError('');
    setNewName('');
    setShowCreate(true);
  };
  const cancelCreate = () => {
    if (creating) return;
    setShowCreate(false);
  };
  const submitCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      setCreateError('Give the Higlo a name.');
      return;
    }
    if (!primaryFamilyID) {
      setCreateError('No primary Hearth — contact support.');
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      const auth = getAuth(app);
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/user/createIgloo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ familyID: primaryFamilyID, newIglooName: trimmed }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        setCreateError(result.error || 'Failed to create Higlo.');
        return;
      }
      setShowCreate(false);
      // The new igloo will appear in the list once the onSnapshot fires. If
      // the endpoint returned its id, jump straight to the detail page.
      if (result?.iglooID) {
        navigate(`/app/higlos/${result.iglooID}`);
      }
    } catch (err) {
      setCreateError('Something went wrong. Try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="px-4 pt-3 pb-6">
      <div className="text-[13px] text-slate-500 mb-4">{summary}</div>

      <IncomingInvites primaryFamilyID={primaryFamilyID} />

      {total === 0 ? (
        <p className="text-[13px] text-slate-500 text-center py-6">
          You haven&rsquo;t joined a Higlo yet. Create one to share a space with
          another family.
        </p>
      ) : (
        <div className="space-y-3 mb-3">
          {entries.map((higlo) => (
            <HigloRow
              key={higlo.iglooID}
              higlo={higlo}
              owningFamilies={owningFamilies}
              onClick={() => navigate(`/app/higlos/${higlo.iglooID}`)}
            />
          ))}
        </div>
      )}

      {/* CTA swaps in place: dashed "+ Start a new Higlo" button when
          collapsed; an inline name-entry card when expanded. The user sees
          a new Higlo appear in the list ABOVE and the slot reverts to the
          button. */}
      {showCreate ? (
        <HCard className="p-4">
          <HField
            id="new-higlo-name"
            label="Name your new Higlo"
            placeholder="e.g. Sunday calls"
            helpText="You can rename it later."
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !creating) { e.preventDefault(); submitCreate(); }
              if (e.key === 'Escape' && !creating) { e.preventDefault(); cancelCreate(); }
            }}
            disabled={creating}
            autoFocus
            className="mb-3"
            error={createError || undefined}
          />
          <div className="grid grid-cols-2 gap-2">
            <HSecondaryButton onClick={cancelCreate} disabled={creating}>
              Cancel
            </HSecondaryButton>
            <HPrimaryButton onClick={submitCreate} disabled={creating}>
              {creating ? 'Creating\u2026' : 'Create'}
            </HPrimaryButton>
          </div>
        </HCard>
      ) : (
        <HCard
          variant="muted"
          className="border-dashed cursor-pointer hover:bg-slate-100 transition-colors"
          onClick={openCreate}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openCreate();
            }
          }}
        >
          <div className="p-4 text-center">
            <div className="text-[13px] font-semibold text-slate-700">
              + Start a new Higlo
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              For a different group of people.
            </div>
          </div>
        </HCard>
      )}
    </div>
  );
}

function HigloRow({ higlo, owningFamilies, onClick }) {
  // useHiglos.ownedMemberships mixes real members (isMember===true) with
  // "+ Add" placeholder rows (isMember===false) so HigloDetail can render
  // a single list. For the tab-level row we only want actual members —
  // otherwise every family the user controls shows up as a chip even on
  // Higlos they aren't actually in.
  const memberRows = (higlo.ownedMemberships || []).filter((m) => m.isMember !== false);
  const externals = higlo.externalFamilies || [];
  const acceptedExternals = externals.filter((e) => e.status !== 'pending');
  const pendingCount = externals.filter((e) => e.status === 'pending').length;

  const hearthCount = memberRows.length + acceptedExternals.length;
  const parts = [`${hearthCount} Hearth${hearthCount === 1 ? '' : 's'}`];
  if (acceptedExternals.length > 0) {
    parts.push(
      `${acceptedExternals.length} other famil${acceptedExternals.length === 1 ? 'y' : 'ies'}`,
    );
  }
  if (pendingCount > 0) {
    parts.push(`${pendingCount} pending`);
  }
  const subline = parts.join(' · ');

  return (
    <HCardButton onClick={onClick} className="p-4">
      <div className="flex items-start gap-3">
        <HigloIcon size="sm" className="flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-semibold text-slate-900 truncate">
            {higlo.name || 'Higlo'}
          </div>
          <div className="text-[12px] text-slate-500 mt-0.5 truncate">
            {subline}
          </div>
          {memberRows.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              {memberRows.map((m) => (
                // TODO: wire status to live device state via useFamiliesData —
                // `in-higlo` (or green) when isOnline && screen?.startsWith(iglooID),
                // otherwise gray (offline / home / sleep / anything else).
                // Hardcoded `status="on"` removed because it was always green
                // regardless of whether the Hearth was actually in this Higlo.
                <HChip key={m.familyID}>
                  {m.familyName || 'Hearth'}
                </HChip>
              ))}
            </div>
          )}
        </div>
        <Chevron />
      </div>
    </HCardButton>
  );
}

function Chevron() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#94a3b8"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="flex-shrink-0 mt-1"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
